import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { toast } from 'sonner';
import CancelSubscriptionModal from '../subscription/CancelSubscriptionModal';
import { subscriptionService } from '../../services/subscriptionService';
import { useUserData } from '../../hooks/useUserData';
import DataExportModal from '../gdpr/DataExportModal';
import AccountDeletionModal from '../gdpr/AccountDeletionModal';

interface User {
  email?: string;
  name?: string;
  avatar?: string;
}

interface AccountPreferencesSectionProps {
  user?: User | null;
}

const AccountPreferencesSection: React.FC<AccountPreferencesSectionProps> = ({ user }) => {
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [processingAlerts, setProcessingAlerts] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  
  const { userInfo, refreshUserInfo } = useUserData();

  const handleSaveProfile = () => {
    // TODO: Implement save profile functionality
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(false);
    setShowDeletionModal(true);
  };

  const handleAccountDeleted = () => {
    // Redirect to logout or home page after account deletion
    toast.success('Account deleted successfully. You will be logged out.');
    // TODO: Implement logout and redirect
  };

  const handleCancelSubscription = async (reason?: string) => {
    setIsLoading(true);
    try {
      const result = await subscriptionService.cancelSubscription({ reason });
      toast.success(result.message);
      if (result.note) {
        toast.info(result.note);
      }
      // Refresh user data to update subscription status
      await refreshUserInfo();
      setShowCancelModal(false);
    } catch (error) {
      console.error('Subscription cancellation failed:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel subscription');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const hasActiveSubscription = userInfo?.subscription_status === 'ACTIVE' && userInfo?.current_package;
  
  const getMembershipType = () => {
    if (userInfo?.current_package) {
      switch (userInfo.current_package.toLowerCase()) {
        case 'starter':
          return 'Starter Plan';
        case 'pro':
          return 'Pro Plan';
        case 'business':
          return 'Business Plan';
        case 'starter_yearly':
          return 'Starter Yearly';
        case 'pro_yearly':
          return 'Pro Yearly';
        case 'business_yearly':
          return 'Business Yearly';
        default:
          return userInfo.current_package;
      }
    }
    return 'FREE';
  };

  return (
    <div className="space-y-6">
      {/* Profile Settings Card */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Profile Settings</CardTitle>
          <p className="text-sm text-gray-600">Update your personal information</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Avatar Section */}
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                {user?.avatar ? (
                  <img src={user.avatar} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-semibold text-lg">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Profile Picture</p>
                <p className="text-xs text-gray-500">Upload a new avatar</p>
                <Button variant="outline" size="sm" className="mt-2 text-xs">
                  Change Photo
                </Button>
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input 
                type="email" 
                value={user?.email || ''} 
                disabled 
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Display Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
              <input 
                type="text" 
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Save Button */}
            <Button 
              onClick={handleSaveProfile}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Settings Card */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Notifications</CardTitle>
          <p className="text-sm text-gray-600">Manage your notification preferences</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Email Notifications Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                <p className="text-xs text-gray-500">Receive updates about your account</p>
              </div>
              <button
                onClick={() => setEmailNotifications(!emailNotifications)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  emailNotifications ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Processing Alerts Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Processing Alerts</p>
                <p className="text-xs text-gray-500">Get notified when files are processed</p>
              </div>
              <button
                onClick={() => setProcessingAlerts(!processingAlerts)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  processingAlerts ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    processingAlerts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy Card */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Data & Privacy</CardTitle>
          <p className="text-sm text-gray-600">Manage your data and privacy settings</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Export Data */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Export Your Data</h4>
              <p className="text-xs text-gray-500 mb-3">
                Download a copy of all your data including processed files and metadata.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => setShowExportModal(true)}
              >
                Request Data Export
              </Button>
            </div>

            {/* Delete Account Section */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Delete Account</h4>
              <p className="text-xs text-gray-500 mb-3">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              
              {!showDeleteConfirm ? (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete My Account
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-xs text-red-800 font-medium mb-2">⚠️ Are you sure?</p>
                    <p className="text-xs text-red-700">
                      This will permanently delete your account, all your files, metadata, and usage history. This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="flex-1"
                      onClick={handleDeleteAccount}
                    >
                      Yes, Delete
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Management Card - Only show if user has active subscription */}
      {hasActiveSubscription && (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-900">Subscription Management</CardTitle>
            <p className="text-sm text-gray-600">Manage your current subscription</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Current Subscription Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Current Plan</h4>
                    <p className="text-sm text-blue-700">{getMembershipType()}</p>
                    {userInfo?.membership_expires_at && (
                      <p className="text-xs text-blue-600 mt-1">
                        Next billing: {new Date(userInfo.membership_expires_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                </div>
              </div>

              {/* Change Plan */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Change Plan</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Upgrade or downgrade your subscription plan at any time.
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/pricing">View Plans & Pricing</a>
                </Button>
              </div>

              {/* Cancel Subscription */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Cancel Subscription</h4>
                <p className="text-xs text-gray-500 mb-3">
                  Cancel your subscription. You'll keep access to membership benefits until your current billing period ends.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  onClick={() => setShowCancelModal(true)}
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : 'Cancel Subscription'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        currentPackage={getMembershipType()}
        membershipExpiresAt={userInfo?.membership_expires_at}
        isLoading={isLoading}
      />

      {/* GDPR Data Export Modal */}
      <DataExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      {/* GDPR Account Deletion Modal */}
      <AccountDeletionModal
        isOpen={showDeletionModal}
        onClose={() => setShowDeletionModal(false)}
        onAccountDeleted={handleAccountDeleted}
      />
    </div>
  );
};

export default AccountPreferencesSection;